/**
 * Where a flight flew between. A flight activity records its departure and
 * arrival airports as Airport places, by id, in `customResponses` beside its
 * number and scheduled times:
 *
 *   customResponses.from_airport = "<entity id of the departure airport>"
 *   customResponses.to_airport   = "<entity id of the arrival airport>"
 *
 * Ids rather than codes so the airport is a real place (its lounges, its
 * restaurants, every other visit to it), and so renaming one never strands the
 * flights that used it. The code shown on screen comes from the place's name.
 */

export const FROM_AIRPORT_KEY = "from_airport";
export const TO_AIRPORT_KEY = "to_airport";

export interface FlightRoute {
  from: string | null;
  to: string | null;
}

function idOrNull(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v : null;
}

/** The airport ids a flight recorded, either side possibly unknown. */
export function flightRoute(customResponses: Record<string, unknown> | null | undefined): FlightRoute {
  return {
    from: idOrNull(customResponses?.[FROM_AIRPORT_KEY]),
    to: idOrNull(customResponses?.[TO_AIRPORT_KEY]),
  };
}

/**
 * The airports a set of flights flew between, so a page can load their names
 * alongside its own places. A flight's airports are often places the trip
 * never visits (the far end of a one-way leg), so they aren't otherwise
 * loaded and the route would read "BOS → ?".
 */
export function routeAirportIds(
  activities: { customResponses?: Record<string, unknown> | null }[],
): string[] {
  const ids: string[] = [];
  for (const a of activities) {
    const { from, to } = flightRoute(a.customResponses);
    if (from) ids.push(from);
    if (to) ids.push(to);
  }
  return ids;
}

/**
 * The short form of an airport's name for a route: the code in its trailing
 * parentheses ("Boston Logan (BOS)" → "BOS"), else the IATA code it was saved
 * with, else the name itself.
 */
export function airportShortName(
  name: string | null | undefined,
  iata?: string | null,
): string | null {
  const m = name?.match(/\(([A-Z0-9]{3,4})\)\s*$/);
  if (m?.[1]) return m[1];
  if (iata && /^[A-Z0-9]{3,4}$/.test(iata)) return iata;
  return name?.trim() || null;
}

/**
 * "MCO → BOS", from the two airports' names. A side that isn't known reads as
 * "?" so a half-filled route still shows which half it has; with neither side
 * known there is no route to show.
 */
export function flightRouteLabel(
  route: FlightRoute,
  nameOf: (id: string) => string | null | undefined,
): string | null {
  if (!route.from && !route.to) return null;
  const side = (id: string | null) => (id ? airportShortName(nameOf(id)) ?? "?" : "?");
  return `${side(route.from)} → ${side(route.to)}`;
}

/**
 * An activity's title with its route: "Flight: BOS → MCO". A name that
 * already spells out a route ("Flight MCO → BOS") is left as it is.
 */
export function titleWithRoute(name: string, routeLabel: string | null): string {
  if (!routeLabel || name.includes("→")) return name;
  return `${name}: ${routeLabel}`;
}

/**
 * Whether an activity is a flight: it carries a flight number or a route.
 * Read from `customResponses`, so it needs no place loaded.
 */
export function isFlightActivity(customResponses: Record<string, unknown> | null | undefined): boolean {
  const { from, to } = flightRoute(customResponses);
  return !!(from || to || idOrNull(customResponses?.flight));
}
